import type { Brand, Locale } from "@borealis/layout-express";
import { useLingui } from "@lingui/react";
import type { MobilityAd } from "@mobility-item-page/types";
import { formatBrand, formatPrice } from "@mobility-item-page/utilities";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { formatMileage } from "src/utils/mileage.ts";

export const useEmailShareUrl = () => {
  const { adId } = useAdViewMeta();
  const ad = useAdViewAd();
  const { mountOrigin, brand, locale } = useAppConfig();
  const { i18n } = useLingui();
  const emailTitleText = useSharingText(ad, brand, locale);

  const shareOnEmailTooltip = i18n._({
    id: "itemPageLayout.socialMedia.email.tooltip",
    message: "Share this ad by email",
    comment: "Share by email tooltip",
  });
  const shareOnEmailTips = i18n._({
    id: "itemPageLayout.socialMedia.email.tips",
    message: "Tips: ",
    comment: "Share by email body title",
  });
  const shareOnEmailBody = i18n._({
    id: "itemPageLayout.socialMedia.email.body",
    message: "I found this on {brand}: ",
    comment: "Share by email body text, with brand name",
    values: { brand: formatBrand(brand) },
  });

  const emailShareBaseUrl = `mailto:?subject=${shareOnEmailTips}${encodeURIComponent(emailTitleText || "")}`;
  const emailShareBody = `&body=${shareOnEmailBody}${encodeURIComponent(`${mountOrigin}/${adId}?taid=Share_ad_email`)}`;

  return { title: shareOnEmailTooltip, url: emailShareBaseUrl + emailShareBody };
};

export const useFacebookShareUrl = () => {
  const { adId } = useAdViewMeta();
  const { mountOrigin } = useAppConfig();
  const { i18n } = useLingui();

  const shareOnFbTooltip = i18n._({
    id: "itemPageLayout.socialMedia.facebook.tooltip",
    message: "Share this ad on Facebook",
    comment: "Share on Facebook tooltip",
  });

  const fbShareUrl = "https://www.facebook.com/sharer/sharer.php?u=";
  const fbUrl = `${fbShareUrl + mountOrigin}/${adId}?taid=Share_ad_facebook`;

  return { title: shareOnFbTooltip, url: fbUrl };
};

export const useTwitterShareUrl = () => {
  const ad = useAdViewAd();
  const { adId } = useAdViewMeta();
  const { mountOrigin, brand, locale } = useAppConfig();
  const { i18n } = useLingui();
  const sharingText = useSharingText(ad, brand, locale);

  const shareOnXTooltip = i18n._({
    id: "itemPageLayout.socialMedia.X.tooltip",
    message: "Share this ad on X",
    comment: "Share on X tooltip",
  });

  const twitterShareBaseUrl = "https://twitter.com/intent/tweet?";
  const twitterUrl = new URLSearchParams();
  const twitterHashTags = getTwitterHashTags(ad);
  if (twitterHashTags) twitterUrl.set("hashtags", twitterHashTags);
  const via = `${brand}_${getTLD(mountOrigin)}`;

  twitterUrl.set("via", via);
  twitterUrl.set("related", via);
  twitterUrl.set("url", `${mountOrigin}/${adId}?taid=Share_ad_twitter`);
  twitterUrl.set("text", sharingText);

  return { title: shareOnXTooltip, url: twitterShareBaseUrl + twitterUrl.toString() };
};

const getTwitterHashTags = (ad: MobilityAd) => {
  switch (ad.adViewType) {
    case "car-new":
    case "car-used":
    case "car-leasing":
    case "car-leasing-business":
    case "car-used-business":
      if (ad.model_and_make) {
        let hashTag = "";
        if (ad.model_and_make.parent?.value) hashTag = hashTag.concat(ad.model_and_make.parent?.value);
        if (ad.model_and_make.value) hashTag = hashTag.concat(ad.model_and_make.value);
        hashTag = hashTag.replaceAll(" ", "").replaceAll("-", "");
        return hashTag === "" ? null : hashTag;
      } else return null;
    case "boat-new-sale":
    case "boat-rent":
    case "boat-sale":
    case "agriculture-tractor":
    case "agriculture-thresher":
    case "motorcycle":
    case "atv":
    case "moped":
    case "snowmobile":
    case "caravan":
    case "mobile-home": {
      let hashTag = "";
      if (ad.make?.value) hashTag = hashTag.concat(ad.make.value);
      if (ad.model) hashTag = hashTag.concat(ad.model);
      hashTag = hashTag.replaceAll(" ", "").replaceAll("-", "");
      return hashTag === "" ? null : hashTag;
    }
    case "agriculture-tools": {
      let hashTag = "";
      if (ad.make) hashTag = hashTag.concat(ad.make);
      if (ad.model) hashTag = hashTag.concat(ad.model);
      hashTag = hashTag.replaceAll(" ", "").replaceAll("-", "");
      return hashTag === "" ? null : hashTag;
    }
    default:
      return null;
  }
};

const useSharingText = (ad: MobilityAd, brand: Brand, locale: Locale) => {
  const price = usePriceText(ad, locale, brand);
  let text = ad.title ?? "";

  switch (ad.adViewType) {
    case "car-new":
    case "car-used":
    case "car-leasing":
    case "car-leasing-business":
    case "motorcycle":
    case "moped":
    case "atv":
    case "snowmobile":
    case "mobile-home":
      if (ad.year) text = text.concat(" ", ad.year.toString());
      if (ad.mileage && ad.mileage > 0) text = text.concat(", ", formatMileage(ad.mileage, locale));
      break;
    case "car-used-business":
    case "boat-new-sale":
    case "boat-rent":
    case "boat-sale":
    case "agriculture-tractor":
    case "agriculture-thresher":
    case "agriculture-tools":
    case "caravan":
      if (ad.year) text = text.concat(" ", ad.year.toString());
      break;
  }

  if (price) text = text.concat(", ", price);

  return text;
};

const usePriceText = (ad: MobilityAd, locale: Locale, brand: Brand) => {
  const { i18n } = useLingui();
  if (!ad.price || !ad) {
    return null;
  }

  switch (ad.adViewType) {
    case "car-leasing":
    case "car-leasing-business": {
      const monthly = formatPrice(ad.price?.monthly ?? 0, locale, brand);
      const initial = ad.price.initial ? formatPrice(ad.price.initial, locale, brand) : null;
      const pricePerMonth = i18n.t({
        id: "itemPageLayout.socialMedia.pricePerMonth",
        message: "{price}/month",
        comment: "Price per month: {price}/month",
        values: { price: monthly },
      });
      return initial ? `${pricePerMonth} + ${initial}` : pricePerMonth;
    }
    case "car-new":
    case "car-used":
    case "car-used-business":
      return formatPrice(ad.price.total ?? 0, locale, brand);
    case "boat-rent": {
      const from = formatPrice(ad.price.from ?? 0, locale, brand);
      if (ad.price.to) {
        const to = formatPrice(ad.price.to ?? 0, locale, brand);
        return `${from} - ${to}`;
      }
      return from;
    }
    case "boat-new-sale":
    case "boat-sale":
    case "boat-wanted":
    case "boat-motor":
    case "boat-dock":
      return formatPrice(ad.price ?? 0, locale, brand);
    case "agriculture-tractor":
    case "agriculture-thresher":
    case "agriculture-tools":
    case "motorcycle":
    case "moped":
    case "atv":
    case "snowmobile":
    case "caravan":
    case "mobile-home":
      return formatPrice(ad.price.net ?? 0, locale, brand);
    default:
      return null;
  }
};

const getTLD = (host: string) => {
  const parts = host.split(".");
  return parts[parts.length - 1].split(":")[0];
};
